import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import isNaN from 'lodash/isNaN'

import {
  forEach,
  startsWith,
  replace,
  reduce,
  capitalize,
  lowerCase,
  concat,
  findIndex,
  snakeCase,
} from 'lodash'
import mapObjIndexed from 'ramda/src/mapObjIndexed'

import { ppsSelector } from '../../../selectors'

import OverallSummary from '../../../components/project-profit-sheet/OverallSummary'

import { selectMode } from '../model'

import { parseFinancial } from '../../../lib/utils'
import { MD, ABOVE_CURRENT_PRICE, WMD } from '../constants'
import { THB_FULL } from '../../../constants'

const fallbackNaN = (number) => (isNaN(number) ? '0.00' : number)

const createBars = ([actual, remaining], suffix, formatter) => [
  {
    value: parseFinancial(actual),
    color: '#5cb85c',
    label: `Actual ${suffix}`,
    formatter: { full: formatter },
  },
  {
    value: parseFinancial(remaining),
    color: '#9be89b',
    label: `Remaining ${suffix}`,
    formatter: { full: formatter },
  },
]

const createMarkers = (initial, suffix, formatter) => [
  {
    value: parseFinancial(initial),
    color: '#404040',
    type: 'solid',
    label: `Approved Plan ${suffix}`,
    formatter: { abbr: formatter, full: formatter },
    trigger: ABOVE_CURRENT_PRICE,
  },
]

/**
 * @param {object} props
 * @param {object} props.profitData
 * @param {string} props.display
 */
function OverallSummaryContainer({ profitData: o, display }) {
  const mode = selectMode(display)
  const type = o['project_type']

  // find dynamic keys (e.g. junior, senior, vp)
  const dynamicKeys = []
  const order = [
    'VP',
    'Asst. VP',
    'Manager',
    'Senior Consultant',
    'Consultant',
    'Junior',
    'Subcontract',
  ]
  const dynamicKeyOrdered = []
  // permanence keys
  const stateKeys = ['forecast', 'actual', 'remaining', 'initial_plan', 'gap']
  forEach(o, (v, k) => {
    if (startsWith(k, '$overall_summary_man_days')) {
      let key = replace(
        k,
        /\$overall_summary_man_days|_forecast|_actual|_initial_plan|_gap|_remaining/g,
        ''
      )
      key = replace(key, '_', '')
      if (!dynamicKeys.includes(key) && key !== '') {
        dynamicKeys.push(key)
      }
    }
  })
  forEach(dynamicKeys, d => {
    const index = findIndex(order, o => snakeCase(lowerCase(o)) === d)
    dynamicKeyOrdered[index] = d
  })
  // reduce the sub level (children)
  const subLevelCalculate = reduce(
    o,
    (acc, v, k) => {
      forEach(dynamicKeyOrdered, (dk, i) => {
        const targetKey = capitalize(lowerCase(dk))
        const regManDays = new RegExp(
          `\\$overall_summary_man_days_${dk}(_forecast|_actual|_initial_plan|_gap|_remaining)`,
          'g'
        )
        if (!replace(k, regManDays, '')) {
          const foundAcc = acc.subManDays.find(a => a && a.record[0] === targetKey)
          const findKey = k.split('_')[k.split('_').length - 1]
          const indexKey = stateKeys.findIndex(sk => sk.includes(findKey))
          if (!foundAcc) {
            const arr = [targetKey]
            arr[indexKey + 1] = v === '-' ? '-' : `${v} MDs`
            acc.subManDays[i] = { record: arr }
          } else {
            foundAcc.record[indexKey + 1] = v === '-' ? '-' : `${v} MDs`
          }
        }
        const regWeightedManDays = new RegExp(
          `\\$overall_summary_weighted_man_days_${dk}(_forecast|_actual|_initial_plan|_gap|_remaining)`,
          'g'
        )
        if (!replace(k, regWeightedManDays, '')) {
          const foundAcc = acc.subWeightedManDays.find(a => a && a.record[0] === targetKey)
          const findKey = k.split('_')[k.split('_').length - 1]
          const indexKey = stateKeys.findIndex(sk => sk.includes(findKey))
          if (!foundAcc) {
            const arr = [targetKey]
            arr[indexKey + 1] = v === '-' ? '-' : `${v} WMDs`
            acc.subWeightedManDays[i] = { record: arr }
          } else {
            foundAcc.record[indexKey + 1] = v === '-' ? '-' : `${v} WMDs`
          }
        }
        if (mode === 'actual') {
          const regCostEmployee = new RegExp(
            `\\$actual_overall_summary_cost_employee_${dk}(_forecast|_actual|_initial_plan|_gap|_remaining)`,
            'g'
          )
          if (!replace(k, regCostEmployee, '')) {
            const foundAcc = acc.subCostEmployee.find(a => a && a.record[0] === targetKey)
            const findKey = k.split('_')[k.split('_').length - 1]
            const indexKey = stateKeys.findIndex(sk => sk.includes(findKey))
            if (!foundAcc) {
              const arr = [targetKey]
              arr[indexKey + 1] = v === '-' ? '-' : `${v} THB`
              acc.subCostEmployee[i] = { record: arr }
            } else {
              foundAcc.record[indexKey + 1] = v === '-' ? '-' : `${v} THB`
            }
          }
        }
      })
      return acc
    },
    { subWeightedManDays: [], subManDays: [], subCostEmployee: [] }
  )

  const subLevel = mapObjIndexed(
    (arr, key) =>
      arr.map(({ record: rec }) => {
        const indicatorByKey = {
          subWeightedManDays: {
            bars: createBars([rec[2], rec[3]], 'WMD', WMD),
            markers: createMarkers(rec[4], 'WMD', WMD),
          },
          subManDays: {
            bars: createBars([rec[2], rec[3]], 'MD', MD),
            markers: createMarkers(rec[4], 'MD', MD),
          },
          subCostEmployee: {
            bars: createBars([rec[2], rec[3]], 'Cost', THB_FULL),
            markers: createMarkers(rec[4], 'Cost', THB_FULL),
          },
        }
        if (type === 'SUPPORT') {
          rec[1] = '-'
          rec[4] = '-'
          rec[5] = '-'
          indicatorByKey.subManDays = rec[2]
          indicatorByKey.subWeightedManDays = rec[2]
        }
        return { record: [rec[0], rec[1], rec[4], indicatorByKey[key], rec[5]] }
      }),
    subLevelCalculate
  )

  const createRecord = ({ label, field, _prefix, suffix, formatter, withMode }) => {
    const prefix = withMode ? `${mode}_` : ''
    return [
      label,
      `${_prefix} ${fallbackNaN(o[`$${prefix}overall_summary_${field}_forecast`])} ${suffix}`,
      `${_prefix} ${fallbackNaN(o[`$${prefix}overall_summary_${field}_initial_plan`])} ${suffix}`,
      {
        bars: createBars(
          [
            fallbackNaN(o[`$${prefix}overall_summary_${field}_actual`]),
            fallbackNaN(o[`$${prefix}overall_summary_${field}_remaining`]),
          ],
          suffix,
          formatter
        ),
        markers: createMarkers(
          fallbackNaN(o[`$${prefix}overall_summary_${field}_initial_plan`]),
          suffix,
          formatter
        ),
      },
      `${_prefix} ${fallbackNaN(o[`$${prefix}overall_summary_${field}_gap`])} ${suffix}`,
    ]
  }

  const Cost = createRecord({
    label: 'Cost',
    field: 'cost',
    suffix: '',
    _prefix: '฿',
    formatter: THB_FULL,
    withMode: true,
  })
  const costEmployee = [
    {
      record: createRecord({
        label: 'Employee',
        field: 'cost_employee',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
        withMode: true,
      }),
      children: subLevel.subCostEmployee,
    },
  ]
  const costSubcontract = [
    {
      record: createRecord({
        label: 'Subcontract',
        field: 'cost_sub_contract',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
  ]
  const costVendor = [
    {
      record: createRecord({
        label: 'Other Service',
        field: 'cost_vendor',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
  ]
  const costSubLicense = [
    {
      record: createRecord({
        label: 'License',
        field: 'cost_license_license',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
    {
      record: createRecord({
        label: 'License/MA',
        field: 'cost_license_ma',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
    {
      record: createRecord({
        label: 'License/Subscription',
        field: 'cost_license_subscription',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
  ]
  const costLicense = [
    {
      record: createRecord({
        label: 'License',
        field: 'cost_license',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
      children: costSubLicense,
    },
  ]
  const costSubHardware = [
    {
      record: createRecord({
        label: 'Hardware',
        field: 'cost_hardware_hardware',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
    {
      record: createRecord({
        label: 'Hardware/MA',
        field: 'cost_hardware_ma',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
  ]
  const costHardware = [
    {
      record: createRecord({
        label: 'Hardware',
        field: 'cost_hardware',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
      children: costSubHardware,
    },
  ]
  const costOther = [
    {
      record: createRecord({
        label: 'Other',
        field: 'cost_other',
        suffix: '',
        _prefix: '฿',
        formatter: THB_FULL,
      }),
    },
  ]

  const Manday = createRecord({
    label: 'Service Man-days',
    field: 'man_days',
    suffix: 'MDs',
    _prefix: '',
    formatter: MD,
  })
  const WManday = createRecord({
    label: 'Service W. Man-days',
    field: 'weighted_man_days',
    suffix: 'WMDs',
    _prefix: '',
    formatter: WMD,
  })

  const costSubLevel = concat(
    costEmployee,
    costSubcontract,
    costVendor,
    costHardware,
    costLicense,
    costOther
  )

  const data = [
    { record: Cost, children: costSubLevel },
    { record: Manday, children: subLevel.subManDays },
    { record: WManday, children: subLevel.subWeightedManDays },
  ]
  return <OverallSummary title="Overall Summary" type={ type } data={ data } />
}

const mapStateToProps = createStructuredSelector({
  profitData: ppsSelector.profitData,
  display: ppsSelector.display,
})

export default connect(mapStateToProps)(OverallSummaryContainer)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/containers/OverallSummaryContainer.js